import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import { changelog, ChangelogEntry } from '../../data/changelog';

// Import icons
import backIcon from '../../assets/icons/back.png';

interface ChangelogScreenProps {
  navigation: NavigationProp<any>;
}

const ChangelogScreen: React.FC<ChangelogScreenProps> = ({ navigation }) => {
  const getChangeTypeColor = (type: string): string => {
    switch (type) {
      case 'feature':
        return '#29BFC0';
      case 'improvement':
        return '#4CAF50';
      case 'bugfix':
        return '#FF9800';
      case 'security':
        return '#E85C5C';
      default:
        return '#666';
    }
  };

  const getChangeTypeIcon = (type: string): string => {
    switch (type) {
      case 'feature':
        return '✨';
      case 'improvement':
        return '⬆️';
      case 'bugfix':
        return '🐛';
      case 'security':
        return '🔒';
      default:
        return '•';
    }
  };

  const getChangeTypeLabel = (type: string): string => {
    switch (type) {
      case 'feature':
        return 'New Feature';
      case 'improvement':
        return 'Improvement';
      case 'bugfix':
        return 'Bug Fix';
      case 'security':
        return 'Security';
      default:
        return type;
    }
  };

  const renderChangelogEntry = (entry: ChangelogEntry, index: number) => {
    const isLatest = index === 0;

    return (
      <View key={entry.version} style={styles.changelogCard}>
        <View style={styles.versionHeader}>
          <View>
            <View style={styles.versionRow}>
              <Text style={styles.versionNumber}>v{entry.version}</Text>
              {isLatest && (
                <View style={styles.latestBadge}>
                  <Text style={styles.latestBadgeText}>Latest</Text>
                </View>
              )}
            </View>
            <Text style={styles.releaseDate}>{entry.date}</Text>
          </View>
        </View>

        <View style={styles.changesContainer}>
          {entry.changes.map((change, changeIndex) => (
            <View key={changeIndex} style={styles.changeItem}>
              <View
                style={[
                  styles.changeTypeTag,
                  { backgroundColor: `${getChangeTypeColor(change.type)}20` },
                ]}
              >
                <Text style={styles.changeIcon}>
                  {getChangeTypeIcon(change.type)}
                </Text>
                <Text
                  style={[
                    styles.changeTypeText,
                    { color: getChangeTypeColor(change.type) },
                  ]}
                >
                  {getChangeTypeLabel(change.type)}
                </Text>
              </View>
              <Text style={styles.changeDescription}>{change.description}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>What's New</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.introSection}>
          <Text style={styles.introTitle}>Release History</Text>
          <Text style={styles.introText}>
            Track all the improvements, features, and fixes we've made to MyFinalKey.
          </Text>
        </View>

        {changelog.map((entry, index) => renderChangelogEntry(entry, index))}

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(50),
    marginTop: ms(20),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  placeholder: {
    width: s(24),
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: ms(20),
  },
  introSection: {
    marginBottom: ms(25),
  },
  introTitle: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(8),
  },
  introText: {
    fontSize: ms(16, 0.3),
    color: '#666',
    lineHeight: ms(24, 0.3),
  },
  changelogCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  versionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E5E5',
  },
  versionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(5),
  },
  versionNumber: {
    fontSize: ms(22, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginRight: ms(10),
  },
  latestBadge: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(5),
    paddingVertical: ms(3),
    paddingHorizontal: ms(10),
  },
  latestBadgeText: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  releaseDate: {
    fontSize: ms(14, 0.3),
    color: '#666',
  },
  changesContainer: {
    gap: ms(15),
  },
  changeItem: {
    marginBottom: ms(5),
  },
  changeTypeTag: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: ms(6),
    paddingVertical: ms(4),
    paddingHorizontal: ms(10),
    marginBottom: ms(8),
  },
  changeIcon: {
    fontSize: ms(14, 0.3),
    marginRight: ms(6),
  },
  changeTypeText: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
  },
  changeDescription: {
    fontSize: ms(15, 0.3),
    color: '#333',
    lineHeight: ms(22, 0.3),
    paddingLeft: ms(5),
  },
  bottomPadding: {
    height: s(40),
  },
});

export default ChangelogScreen;
